import { Helper } from "../utils/Helper";
import { AssetLoader } from "@nolimitcity/slot-game/bin/asset/AssetLoader";
import { Logger } from "@nolimitcity/slot-launcher/bin/utils/Logger";
import { gsap, Power3 } from "gsap";

export interface SpinePlayConfig {
    animation: string;

    track?: number;
    duration?: number;
    delay?: number;

    skinType?: string;

    loop?: boolean;

    freezed?: boolean;

    showOnEnd?: boolean;

    onStart?: Function;
    onComplete?: Function;
    onUpdate?: Function;
}

export class SpinePlayer extends PIXI.Container {

    private _tl: gsap.core.Timeline;
    private _tlSwitch: gsap.core.Timeline;
    private _spine: PIXI.spine.Spine | undefined;
    private _spineSwitch: PIXI.spine.Spine | undefined;

    private _path: string;
    private static loader: AssetLoader;

    constructor(spinePath: string, initVisible: boolean = false) {
        super();

        this.changeSpine(spinePath);

        this.visible = initVisible;
    }

    public static setSpineLoader(loader: AssetLoader): void { this.loader = loader }

    public get spine(): PIXI.spine.Spine {
        return this._spine!;
    }

    public get animations(): string[] {
        return SpinePlayer.fetchAnimations(this._path);
    }

    public get timeLine(): gsap.core.Timeline {
        return this._tl;
    }

    public get isPlaying(): boolean {
        return !!this._tl && this._tl.isActive();
    }

    public animateSpineSwitch(toPath: string, conf: SpinePlayConfig, fadeDurr: number): gsap.core.Timeline {
        this._tlSwitch = Helper.setTimeline(this._tlSwitch, new gsap.core.Timeline({
            onComplete: () => this.removeSpineSwitch()
        }));

        this._spineSwitch = this._spine;

        this._path = toPath;

        // this call replaces the "this._spine"
        this._tlSwitch.add(this.play(conf), 0);

        this._tlSwitch.fromTo(this._spine!, { alpha: 0 }, { alpha: 1, duration: fadeDurr, ease: "power3.inOut" }, 0);
        this._tlSwitch.fromTo(this._spineSwitch!, { alpha: 1 }, { alpha: 0, duration: fadeDurr, ease: "power3.inOut" }, 0);

        return this._tlSwitch;
    }

    private removeSpineSwitch(): void {
        if (this._spineSwitch) {
            this._spineSwitch.destroy({ children: true });
            this.removeChildren();
            delete this._spineSwitch;
        }
    }

    public changeSpine(path: string): void {
        if (this._path == path) return;

        this._path = path;

        this.reset();
    }

    public play(conf?: SpinePlayConfig): gsap.core.Timeline {
        if (!conf) conf = { animation: this._path };

        this.reset();
        this._tl = Helper.setTimeline(this._tl, new gsap.core.Timeline({
            onStart: () => {
                this.visible = true;
                conf?.onStart && conf.onStart();
            },
            onComplete: () => {
                this.visible = !!conf?.showOnEnd;
                conf?.onComplete && conf.onComplete();
            },
            repeat: !!conf.loop ? -1 : 0
        }));

        if (conf.skinType) this.spine.skeleton.setSkinByName(conf.skinType);

        this.spine.state.addAnimation(conf.track === undefined ? 0 : conf.track, conf.animation, !!conf.loop, conf.delay === undefined ? 0 : conf.delay);
        const durr: number = conf.duration === undefined ? this.fetchDuration(conf.animation) : conf.duration;
        this.spine.state.timeScale = this.fetchDuration(conf.animation) / durr;
        this._tl.to(this, { duration: durr });
        if(conf.freezed){
            this._tl.pause();
            this.spine.state.timeScale = 0;
        } 
        return this._tl;
    }

    public stop(playToEnd?: boolean): void {
        this._tl = Helper.setTimeline(this._tl, undefined, playToEnd)!;
        this.visible = false;
        this.reset();
    }

    public fetchDuration(animationName: string): number {
        return SpinePlayer.fetchDuration(this._path, animationName);
    }

    public static fetchDuration(_spinePath: string, animationName: string): number {
        const durr: number | undefined = SpinePlayer.loader.getSpineAsset(_spinePath).animations.find(anim => { return anim.name === animationName })?.duration;

        if (!durr) {
            Logger.warn("Couldn't find animation: " + animationName + " in Spine" + _spinePath);
            return 0;
        }

        return durr;
    }

    public static fetchAnimations(_spinePath: string): string[] {
        return SpinePlayer.loader.getSpineAsset(_spinePath).animations.map((v) => v.name);
    }

    public reset(): void {
        if (this.spine && this.parent) { // #342
            this.spine.destroy({ children: true });
            this.removeChildren();
            delete this._spine;
        }
        this._spine = this.createSpine();
    }

    protected createSpine(): PIXI.spine.Spine {
        const spine: PIXI.spine.Spine = this.addChild(new PIXI.spine.Spine(SpinePlayer.loader.getSpineAsset(this._path)));
        return spine;
    }

    public onResize?(): void;
}